import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18n from 'i18next';
import toast from 'react-hot-toast';
import { store } from '../app/store';
import { clearAccount } from '../data/accountSlice';
import { clearUserCache } from '../utils/storage';

// Define the standard response structure
export interface ApiResponse<T = any> {
  code: number;
  message?: string;
  msg?: string; // Support backend returning 'msg'
  data: T;
  traceId?: string;
}

const baseURL = process.env.REACT_APP_PUBLIC_API_URL || '';

const client: AxiosInstance = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
client.interceptors.request.use(
  (config) => {
    // Add Accept-Language header
    // Use resolvedLanguage to ensure we send a supported language (e.g., zh-TW instead of zh-CN)
    // If resolvedLanguage is not available, fallback to language or 'zh-TW'
    const lang = i18n.resolvedLanguage || i18n.language || 'zh-TW';
    config.headers['Accept-Language'] = lang === 'zh-CN' ? 'zh-TW' : lang;

    // Add Authorization header if token exists
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
client.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { code, message, msg } = response.data;
    // Some APIs might return success without code 200, check if code exists
    if (code !== undefined && code !== 200) {
      const errorMsg = message || msg || 'Error';
      
      // Specific handling for 401 (Unauthorized) in business code
      if (code === 401) {
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('jwt_expire');
        store.dispatch(clearAccount());
        
        // Use a more user-friendly message for 401
        // Prevent duplicate toasts for session expiry
        const sessionExpiredMsg = i18n.t('common.sessionExpired', { defaultValue: '登录已过期，请重新登录' });
        
        // We reject with a specific error object that can be identified
        const error = new Error(sessionExpiredMsg);
        (error as any).isSessionExpired = true; // Flag to identify this specific error
        
        // Only show toast if it hasn't been shown recently
        toast.error(sessionExpiredMsg, { id: 'session-expired' });
        
        // Return a pending promise to halt the promise chain, preventing further .catch() blocks in components from firing generic error toasts
        // effectively "swallowing" the error from the perspective of the calling code
        return new Promise(() => {}); 
      }
      
      // Show error toast
      toast.error(errorMsg);
      
      // Reject with the message so components can handle it
      return Promise.reject(new Error(errorMsg));
    }

    return response;
  },
  (error) => {
    // Handle network errors or HTTP errors (like 401, 403, 500)
    let errorMsg = 'Unknown Error';
    
    if (error.response) {
      const { status, data } = error.response;
      
      // Handle 401 Unauthorized (HTTP status)
      if (status === 401) {
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('jwt_expire');
        store.dispatch(clearAccount());
        
        const sessionExpiredMsg = i18n.t('common.sessionExpired', { defaultValue: '登录已过期，请重新登录' });
        
        // Prevent duplicate toasts using a constant ID
        toast.error(sessionExpiredMsg, { id: 'session-expired' });
        
        // Return a pending promise to halt execution flow
        return new Promise(() => {});
      }
      
      // Try to extract backend error message if available
      if (data && (data.message || data.msg)) {
        errorMsg = data.message || data.msg;
      } else {
        errorMsg = error.message || 'Request failed';
      }
      
      toast.error(errorMsg);
      return Promise.reject(new Error(errorMsg));
    }
    
    errorMsg = error.message || 'Network Error';
    toast.error(errorMsg);
    return Promise.reject(error);
  }
);

export default client;
